import { useCallback, useRef } from 'react'

import { ProspectAuditStatus, ProspectsService } from '@api'
import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'

const MAX_ATTEMPTS = 24
const TIMEOUT = 5000

export const useAuditProspect = () => {
  const creatorId = useUnit($creatorId)
  const attemptRef = useRef(0)

  const auditMutation = useMutation(
    (prospectId: number) =>
      ProspectsService.auditProspect({ creatorId: creatorId ?? 0, prospectId }),
    {
      onError: (error) => handleError(error, 'Failed to audit prospect'),
    },
  )

  const auditProspect = useCallback(
    async (prospectId: number) => {
      attemptRef.current = 0

      const audit = async () => {
        const res = await auditMutation.mutateAsync(prospectId)

        if (res.status === ProspectAuditStatus.RUNNING) {
          if (attemptRef.current >= MAX_ATTEMPTS) {
            attemptRef.current = 0
            throw new Error('Audit is taking too long. Please try again later.')
          }
          await new Promise((resolve) => setTimeout(resolve, TIMEOUT))
          attemptRef.current += 1

          return audit()
        }

        return res
      }

      return audit()
    },
    [auditMutation],
  )

  return {
    auditProspect,
    isAuditing: auditMutation.isLoading,
  }
}
